import * as Dialog from '@radix-ui/react-dialog';
import { useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { getVideoViews } from './videoViews';

function PreviewPlayer({ view, initiallyMuted, register, onAudio }) {
    const [failed, setFailed] = useState(false);
    return (
        <section className="min-w-0">
            <h3 className="mb-2 text-sm font-semibold">{view.label}</h3>
            <video
                ref={register}
                aria-label={view.label}
                src={view.url}
                controls
                playsInline
                preload="metadata"
                muted={initiallyMuted}
                controlsList="nodownload"
                onVolumeChange={onAudio}
                onError={() => setFailed(true)}
                className="w-full rounded-md bg-black"
                style={{ height: 'min(55vh, 440px)', objectFit: 'contain' }}
            />
            {failed && <p role="alert" className="mt-2 text-sm text-red-600">
                Could not load {view.label.toLowerCase()}. Please reopen the preview to retry.
            </p>}
        </section>
    );
}

export function VideoModal({ isOpen, onClose, video, videoUrl }) {
    const players = useRef({});
    const views = getVideoViews(video || { videoUrl });
    const handleAudio = (id) => {
        const active = players.current[id];
        if (!active || active.muted || active.volume === 0) return;
        Object.entries(players.current).forEach(([otherId, player]) => {
            if (otherId !== id && player) player.muted = true;
        });
    };
    return (
        <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-black bg-opacity-30" />
                <Dialog.Content className={cn(
                    'fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-6 bg-white rounded-md',
                    'w-[calc(100%_-_2rem)] max-h-[90vh] overflow-y-auto dark:bg-gray-800 dark:text-gray-100',
                    views.length > 1 ? 'max-w-3xl' : 'max-w-md'
                )}>
                    <div className="flex mb-4 items-center justify-between">
                        <Dialog.Title className="text-xl font-bold">Video Preview</Dialog.Title>
                        <Dialog.Close asChild>
                            <button aria-label="Close video preview" className="rounded-full p-1 bg-gray-100 dark:bg-gray-900">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6 6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </Dialog.Close>
                    </div>
                    <Dialog.Description className="mb-4 text-sm">
                        {views.length > 1
                            ? 'Both camera views are available below. Use each player to review it. Only one camera plays sound at a time.'
                            : 'Use the player below to review this RECC.'}
                    </Dialog.Description>
                    {isOpen && (views.length ? (
                        <div className={cn('grid gap-4', views.length > 1 && 'sm:grid-cols-2')}>
                            {views.map((view, index) => (
                                <PreviewPlayer key={view.url} view={view} initiallyMuted={index > 0}
                                    register={(player) => { players.current[view.id] = player; }}
                                    onAudio={() => handleAudio(view.id)} />
                            ))}
                        </div>
                    ) : <p role="status">This RECC is no longer available.</p>)}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
